import styled from '@emotion/styled';
import { useCallback } from 'react';
import { Color } from '@diritto/common/style/color';
import { FontWeight, Typography } from '@diritto/common/style/typography';
import { SectionRelayDto } from '@diritto/common/relay/dtos/section-relay.dtos';
import { TrackerExtraInfoProps, TrackingEventType } from '@diritto/common/tracker/tracking-event.constant';
import { ContentBodyPadding } from 'constants/layout';
import { Link } from 'ui-components/common/Link';
import { useTrackingState } from 'contexts/TrackingProvider';

type PageTitleProps = {
  section: SectionRelayDto;
  moreLink?: string;
  description?: string;
};

export const SectionTitle = ({
  section,
  moreLink,
  description,
  trackerExtraInfo,
}: PageTitleProps & TrackerExtraInfoProps) => {
  const { tracker } = useTrackingState();
  const handleOnClickTracker = useCallback(() => {
    tracker.track(TrackingEventType.ClickMoreLinkOnHomeSection, {
      sectionId: section.id,
      title: section.title,
      trackerExtraInfo,
    });
  }, [section.id, section.title, tracker, trackerExtraInfo]);

  if (!moreLink && !section.title) {
    return null;
  }

  return (
    <StyledContentWrapper>
      <SectionTitleWrapper>
        <StyledTitle>{section.title}</StyledTitle>
        {description && <StyledDescription>{description}</StyledDescription>}
      </SectionTitleWrapper>
      {moreLink && (
        <StyledMoreLink href={moreLink} onClick={handleOnClickTracker}>
          더보기
        </StyledMoreLink>
      )}
    </StyledContentWrapper>
  );
};

const StyledContentWrapper = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  padding: 0 ${ContentBodyPadding}px;
  margin-bottom: 16px;
  white-space: pre-wrap;
`;

const StyledTitle = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  color: ${Color.Black};
  font-weight: ${FontWeight.Bold};
  ${Typography.Small};
  margin-bottom: 4px;
`;

const SectionTitleWrapper = styled.div`
  margin-top: 4px;
  flex-grow: 1;
`;

const StyledDescription = styled.div`
  color: ${Color.Gray600};
  font-weight: ${FontWeight.Regular};
  ${Typography.XXXSmall};
`;

const StyledMoreLink = styled(Link)`
  flex-grow: 0;
  display: flex;
  align-items: center;
  color: ${Color.Gray500};
  font-weight: ${FontWeight.Medium};
  ${Typography.XXSmall};
`;
